import { FC, useState, Fragment } from 'react'
import { Button, IconButton, Input, Spinner, Typography } from '@material-tailwind/react'
import { PiPlus, PiTrash } from 'react-icons/pi'
import { v4 as uuidv4 } from 'uuid'
import { useTranslation } from 'react-i18next'
import { colors } from '@material-tailwind/react/types/generic'
import { logger } from '../utils/helpers/logger'
import { cleanFileName } from '../utils/helpers/upload.helpers'
import { CREDITS_ASSETS_BUCKET } from '../utils/constants/db.constants'
import { removeFile, supabase, uploadFile } from '../utils/helpers/supabase.helpers'
import { useBucketInfo } from '../hooks/useBucketInfo'
import { useDarkMode } from '../contexts/DarkModeContext'
import { useGeneralToast } from '../hooks/toast'
import { useSessionContext } from '../contexts/SessionContext'

interface ImageBlock {
  id: string
  url: string
  storage_path: string | null
  alt: string
}

interface CreditBlock {
  id: string
  type: 'text' | 'images'
  text?: string
  images?: ImageBlock[]
}

interface ExperienceCreditsEditorImageBlockProps {
  experienceId: string
  creditsBlocks: CreditBlock[]
  block: CreditBlock
  onUpdateBlocks: (blocks: CreditBlock[]) => void
}

export const ExperienceCreditsEditorImageBlock: FC<ExperienceCreditsEditorImageBlockProps> = ({
  experienceId,
  creditsBlocks,
  block,
  onUpdateBlocks,
}) => {
  const { darkMode } = useDarkMode()
  const { t } = useTranslation()
  const { user } = useSessionContext()
  const {
    isLoading: bucketLoading,
    uploadSizeLimit,
    formattedUploadLimitInMB,
    allowedFileTypes,
    allowedFileExtensions,
  } = useBucketInfo({ bucket: CREDITS_ASSETS_BUCKET, enabled: true })

  const { error: toastError } = useGeneralToast()

  const [imageUploadStatus, setImageUploadStatus] = useState<Record<string, boolean>>({})

  const isImageUploading = (imageId: string) => Boolean(imageUploadStatus[imageId])

  const findImage = (blockId: string, imageId: string) => {
    const currentBlock = (creditsBlocks ?? []).find((b) => b.id === blockId)
    if (!currentBlock) return undefined
    return (currentBlock.images ?? []).find((image) => image.id === imageId)
  }

  const setImageUploading = (imageId: string, isUploading: boolean) => {
    setImageUploadStatus((prev) => {
      if (!isUploading) {
        if (!(imageId in prev)) return prev
        const next = { ...prev }
        delete next[imageId]
        return next
      }
      return { ...prev, [imageId]: true }
    })
  }

  const handleImageFileUpload = async (blockId: string, imageId: string, file: File) => {
    if (!file) return

    if (!(allowedFileTypes as string[]).includes(file.type)) {
      toastError(t('experienceCreditsEditor.wrongImageFormat'))
      return
    }

    if (file.size > uploadSizeLimit) {
      toastError(`${t('experienceCreditsEditor.imageTooBig')} ${formattedUploadLimitInMB}`)
      return
    }

    if (!experienceId) {
      toastError(t('experienceCreditsEditor.missingExperience'))
      return
    }

    const sanitizedFileName = cleanFileName({ file }) ?? file.name
    const filename = `${user?.id}/${experienceId}/${imageId}-${sanitizedFileName}`

    setImageUploading(imageId, true)

    try {
      const { data, error } = await uploadFile({
        bucketId: CREDITS_ASSETS_BUCKET,
        file,
        filename,
        contentType: file.type,
        cacheControl: '3600',
        upsert: true,
      })

      if (error) {
        toastError(t('experienceCreditsEditor.uploadError'))
        return
      }

      const uploadedPath = (data as { path?: string })?.path ?? filename

      const { data: publicUrlData } = supabase.storage.from(CREDITS_ASSETS_BUCKET).getPublicUrl(uploadedPath)

      // Note: getPublicUrl doesn't return an error in the current Supabase version
      if (!publicUrlData) {
        toastError(t('experienceCreditsEditor.urlError'))
        return
      }

      const publicUrl = publicUrlData?.publicUrl

      if (!publicUrl) {
        toastError(t('experienceCreditsEditor.imageUnreachable'))
        return
      }

      const currentImage = findImage(blockId, imageId)

      const blocks = (creditsBlocks ?? []).map((currentBlock) => {
        if (currentBlock.id !== blockId) return currentBlock
        const images = (currentBlock.images ?? []).map((image) =>
          image.id === imageId
            ? {
                ...image,
                url: publicUrl,
                storage_path: uploadedPath,
              }
            : image
        )
        return { ...currentBlock, images }
      })

      onUpdateBlocks(blocks)

      if (currentImage?.storage_path && currentImage.storage_path !== uploadedPath) {
        const { error: removeError } = await removeFile({
          bucketId: CREDITS_ASSETS_BUCKET,
          filename: currentImage.storage_path,
        })
        if (removeError) {
        }
      }
    } catch (err) {
      logger.error(err, 'UploadCreditsImage')
      toastError(t('experienceCreditsEditor.unexpectedUploadError'))
    } finally {
      setImageUploading(imageId, false)
    }
  }

  const addImage = (blockId: string) => {
    const blocks = (creditsBlocks ?? []).map((b) =>
      b.id === blockId
        ? {
            ...b,
            images: [...(b.images ?? []), { id: uuidv4(), url: '', storage_path: null, alt: '' }],
          }
        : b
    )
    onUpdateBlocks(blocks)
  }

  const updateImage = (blockId: string, imageId: string, keyOrData: string | object, value?: string) => {
    const patch = typeof keyOrData === 'string' ? { [keyOrData]: value } : (keyOrData ?? {})
    const blocks = (creditsBlocks ?? []).map((b) => {
      if (b.id !== blockId) return b
      const images = (b.images ?? []).map((l) => (l.id === imageId ? { ...l, ...patch } : l))
      return { ...b, images }
    })
    onUpdateBlocks(blocks)
  }

  const removeImage = async (blockId: string, imageId: string) => {
    const targetImage = findImage(blockId, imageId)

    if (targetImage?.storage_path) {
      const { error: storageError } = await removeFile({
        bucketId: CREDITS_ASSETS_BUCKET,
        filename: targetImage.storage_path,
      })
      if (storageError) {
        toastError('Unable to remove the image from storage.')
      }
    }

    setImageUploading(imageId, false)

    const blocks = (creditsBlocks ?? []).map((b) => {
      if (b.id !== blockId) return b
      const images = (b.images ?? []).filter((l) => l.id !== imageId)
      return { ...b, images }
    })
    onUpdateBlocks(blocks)
  }

  return (
    <div className="flex flex-col gap-3">
      {(block.images ?? []).map((image) => (
        <div key={image.id} className="grid grid-cols-1 md:grid-cols-1 gap-3 items-start">
          <hr className="w-full my-1 opacity-20 border-primary dark:border-white" />
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <div className="flex justify-between">
                <Typography className="font-bold">Image</Typography>
                <IconButton
                  size="sm"
                  variant="text"
                  color={darkMode ? 'white' : 'gray'}
                  onClick={() => removeImage(block.id, image.id)}
                  title="Remove image"
                  disabled={isImageUploading(image.id)}
                >
                  <PiTrash className="h-4 w-4" />
                </IconButton>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex flex-row gap-2 items-end">
                {image.url && (
                  <div className="h-24 w-32 flex items-center justify-center border border-gray-200 dark:border-base-gray-600 rounded-md bg-white dark:bg-base-gray-700 overflow-hidden">
                    <a href={image.url} target="_blank" rel="noreferrer">
                      <img
                        src={image.url}
                        alt={image.alt || 'Image preview'}
                        className="h-auto w-full object-contain"
                      />
                    </a>
                  </div>
                )}
                <Input
                  id={`image-upload-${image.id}`}
                  label={
                    image.url
                      ? t('experienceCreditsEditor.updateImage')
                      : t('experienceCreditsEditor.uploadImage')
                  }
                  accept={allowedFileTypes.join(',')}
                  type="file"
                  onChange={(e) => {
                    const { files } = e.target
                    const selectedFile = files ? files[0] : undefined
                    if (selectedFile) {
                      handleImageFileUpload(block.id, image.id, selectedFile)
                    }
                    e.target.value = ''
                  }}
                  disabled={isImageUploading(image.id)}
                />
              </div>
              <Typography variant="small">
                {t('mediaFileDropzone.fileSizeLimit')}:{' '}
                <em className="font-bold">{formattedUploadLimitInMB}</em>
                {allowedFileTypes && (
                  <>
                    {' / '}
                    {t('mediaFileDropzone.allowedFileTypes')}:{' '}
                    <em className="font-bold">
                      {allowedFileExtensions?.map((filetype) => (
                        <Fragment key={filetype}>
                          <span>{filetype}</span>{' '}
                        </Fragment>
                      ))}
                    </em>
                  </>
                )}
              </Typography>
            </div>

            {isImageUploading(image.id) && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Spinner className="h-4 w-4" />
              </div>
            )}
          </div>
          <Input
            label={t('experienceCreditsEditor.altText')}
            value={image.alt ?? ''}
            onChange={(e) => updateImage(block.id, image.id, 'alt', e.currentTarget.value ?? '')}
            disabled={isImageUploading(image.id)}
            {...(darkMode && { color: 'white' })}
          />
        </div>
      ))}

      <div>
        <Button
          size="sm"
          color={(darkMode ? 'accent' : 'primary') as colors}
          variant="outlined"
          className="flex items-center gap-2 uppercase"
          onClick={() => addImage(block.id)}
          disabled={bucketLoading}
          loading={bucketLoading}
        >
          <PiPlus /> {t('experienceCreditsEditor.addImage')}
        </Button>
      </div>
    </div>
  )
}
